
import { SQLModule } from './types';

export const phase6Modules: SQLModule[] = [
  {
    id: "healthcare-data-compliance",
    title: "Healthcare Data Management & HIPAA Compliance",
    description: "Master healthcare data management with HIPAA compliance, patient privacy protection, and medical data analytics using SQL.",
    order: 1,
    content: `
# Healthcare Data Management & HIPAA Compliance

## Overview
Healthcare data management requires strict compliance with HIPAA regulations while enabling efficient data analysis for patient care and research. This module covers secure healthcare database design and compliant data handling.

## Key Concepts

### HIPAA Compliance Requirements
- **Protected Health Information (PHI)**: Identifying patient data elements
- **De-identification Techniques**: Safe Harbor and Expert Determination methods
- **Access Controls**: Role-based access for healthcare personnel
- **Audit Trails**: Tracking all data access and modifications
- **Data Encryption**: Protecting PHI at rest and in transit

### Healthcare Database Design
- **Patient Management Systems**: Electronic Health Records (EHR) design
- **Clinical Data Warehouses**: Longitudinal patient data storage
- **Medical Coding Systems**: ICD-10, CPT, SNOMED integration
- **Laboratory Information Systems**: Test results and reference ranges

## Practical Examples

### HIPAA-Compliant Table Design
\`\`\`sql
-- Patient demographics with PHI protection
CREATE TABLE patients (
  patient_id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  medical_record_number VARCHAR(20) UNIQUE NOT NULL,
  first_name_encrypted BYTEA, -- Encrypted PHI
  last_name_encrypted BYTEA,
  date_of_birth_encrypted BYTEA,
  ssn_encrypted BYTEA,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  created_by VARCHAR(50) NOT NULL,
  last_accessed TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Audit log for all patient data access
CREATE TABLE patient_access_log (
  log_id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  patient_id UUID REFERENCES patients(patient_id),
  user_id VARCHAR(50) NOT NULL,
  access_type VARCHAR(20) NOT NULL, -- SELECT, INSERT, UPDATE, DELETE
  access_timestamp TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  ip_address INET,
  user_agent TEXT,
  reason_for_access TEXT
);
\`\`\`

### Medical Data Analytics
\`\`\`sql
-- Patient outcome analysis (de-identified)
WITH patient_outcomes AS (
  SELECT 
    EXTRACT(YEAR FROM p.date_of_birth_encrypted) - 10 AS birth_decade, -- Age grouping
    d.diagnosis_code,
    t.treatment_code,
    CASE 
      WHEN o.outcome_score >= 80 THEN 'Excellent'
      WHEN o.outcome_score >= 60 THEN 'Good'
      WHEN o.outcome_score >= 40 THEN 'Fair'
      ELSE 'Poor'
    END AS outcome_category
  FROM patients p
  JOIN diagnoses d ON p.patient_id = d.patient_id
  JOIN treatments t ON p.patient_id = t.patient_id
  JOIN outcomes o ON p.patient_id = o.patient_id
  WHERE d.diagnosis_date >= CURRENT_DATE - INTERVAL '1 year'
)
SELECT 
  birth_decade,
  diagnosis_code,
  treatment_code,
  outcome_category,
  COUNT(*) as patient_count
FROM patient_outcomes
GROUP BY birth_decade, diagnosis_code, treatment_code, outcome_category
ORDER BY patient_count DESC;
\`\`\`

## Advanced Healthcare Analytics

### Clinical Decision Support
- **Risk Stratification Models**: Identifying high-risk patients
- **Medication Interaction Checking**: Drug safety monitoring
- **Clinical Quality Measures**: Performance indicator tracking
- **Population Health Analytics**: Community health trends

### Regulatory Reporting
- **CMS Quality Reporting**: Medicare quality measures
- **Joint Commission Metrics**: Hospital accreditation requirements
- **Public Health Reporting**: Disease surveillance and outbreak detection
- **Research Data Compliance**: IRB and research protocol adherence
    `,
    estimatedTime: 90,
    difficulty: "advanced",
    prerequisites: ["Advanced SQL", "Healthcare domain knowledge", "Data privacy regulations"],
    tags: ["Healthcare", "HIPAA", "Compliance", "Medical Data", "PHI Protection"],
    videoUrl: "https://www.youtube.com/embed/HXV3zeQKqGY", // Healthcare SQL Analytics
    duration: "90 minutes"
  },
  {
    id: "financial-services-compliance",
    title: "Financial Services & Regulatory Compliance",
    description: "Implement financial data management systems with AML/KYC compliance, risk management, and regulatory reporting using SQL.",
    order: 2,
    content: `
# Financial Services & Regulatory Compliance

## Overview
Financial services require robust data management systems that comply with regulatory requirements while enabling real-time risk assessment and fraud detection. This module covers financial database design and compliance frameworks.

## Key Concepts

### Regulatory Frameworks
- **AML (Anti-Money Laundering)**: Transaction monitoring and suspicious activity reporting
- **KYC (Know Your Customer)**: Customer due diligence and identity verification
- **PCI DSS**: Payment card data security standards
- **Basel III**: Capital adequacy and risk management
- **GDPR**: Customer data protection in financial services

### Financial Data Architecture
- **Transaction Processing Systems**: High-frequency trading data
- **Risk Management Platforms**: Credit risk and market risk assessment
- **Compliance Monitoring**: Real-time transaction surveillance
- **Customer Analytics**: Behavioral analysis and segmentation

## Practical Examples

### Transaction Monitoring System
\`\`\`sql
-- Suspicious transaction detection
CREATE TABLE transactions (
  transaction_id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  account_id VARCHAR(20) NOT NULL,
  transaction_type VARCHAR(20) NOT NULL,
  amount DECIMAL(15,2) NOT NULL,
  currency_code CHAR(3) NOT NULL,
  transaction_date TIMESTAMP NOT NULL,
  counterparty_account VARCHAR(20),
  description TEXT,
  risk_score INTEGER DEFAULT 0,
  flagged_for_review BOOLEAN DEFAULT FALSE,
  reviewed_by VARCHAR(50),
  review_date TIMESTAMP
);

-- AML monitoring rules
WITH suspicious_patterns AS (
  SELECT 
    account_id,
    COUNT(*) as transaction_count,
    SUM(amount) as total_amount,
    AVG(amount) as avg_amount,
    MAX(amount) as max_amount
  FROM transactions
  WHERE transaction_date >= CURRENT_DATE - INTERVAL '7 days'
  GROUP BY account_id
)
SELECT 
  sp.account_id,
  sp.transaction_count,
  sp.total_amount,
  CASE 
    WHEN sp.total_amount > 10000 AND sp.transaction_count > 20 THEN 'HIGH_RISK'
    WHEN sp.max_amount > 5000 AND sp.avg_amount < 100 THEN 'STRUCTURING'
    WHEN sp.transaction_count > 50 THEN 'VELOCITY_RISK'
    ELSE 'NORMAL'
  END as risk_category
FROM suspicious_patterns sp
WHERE sp.total_amount > 5000 OR sp.transaction_count > 10
ORDER BY sp.total_amount DESC;
\`\`\`

### Credit Risk Assessment
\`\`\`sql
-- Credit scoring model
WITH customer_metrics AS (
  SELECT 
    c.customer_id,
    c.credit_score,
    COUNT(l.loan_id) as total_loans,
    AVG(l.amount) as avg_loan_amount,
    SUM(CASE WHEN l.status = 'DEFAULTED' THEN 1 ELSE 0 END) as defaults,
    AVG(p.days_late) as avg_payment_delay
  FROM customers c
  LEFT JOIN loans l ON c.customer_id = l.customer_id
  LEFT JOIN payments p ON l.loan_id = p.loan_id
  GROUP BY c.customer_id, c.credit_score
)
SELECT 
  customer_id,
  credit_score,
  total_loans,
  avg_loan_amount,
  defaults,
  avg_payment_delay,
  CASE 
    WHEN credit_score >= 750 AND defaults = 0 THEN 'PRIME'
    WHEN credit_score >= 650 AND defaults <= 1 THEN 'NEAR_PRIME'
    WHEN credit_score >= 550 AND defaults <= 2 THEN 'SUBPRIME'
    ELSE 'HIGH_RISK'
  END as risk_tier
FROM customer_metrics
WHERE total_loans > 0;
\`\`\`

## Advanced Financial Analytics

### Market Risk Management
- **Value at Risk (VaR)**: Portfolio risk measurement
- **Stress Testing**: Scenario analysis and sensitivity testing
- **Liquidity Risk**: Cash flow and funding analysis
- **Operational Risk**: Process failure and fraud detection

### Regulatory Reporting
- **CCAR/DFAST**: Comprehensive capital analysis and review
- **Liquidity Coverage Ratio**: Basel III liquidity requirements
- **Suspicious Activity Reports**: AML compliance reporting
- **Trade Surveillance**: Market manipulation detection
    `,
    estimatedTime: 85,
    difficulty: "advanced",
    prerequisites: ["Advanced SQL", "Financial services knowledge", "Regulatory compliance awareness"],
    tags: ["Finance", "AML", "KYC", "Risk Management", "Compliance", "Fraud Detection"],
    videoUrl: "https://www.youtube.com/embed/9yeOJ0ZMUYw", // Financial SQL Analytics
    duration: "85 minutes"
  },
  {
    id: "ecommerce-analytics-optimization",
    title: "E-commerce Analytics & Business Intelligence",
    description: "Build comprehensive e-commerce analytics systems for customer behavior analysis, sales optimization, and business intelligence using SQL.",
    order: 3,
    content: `
# E-commerce Analytics & Business Intelligence

## Overview
E-commerce platforms generate massive amounts of data that can drive business decisions. This module covers building analytics systems for customer insights, sales optimization, and operational intelligence.

## Key Concepts

### E-commerce Data Architecture
- **Customer Journey Tracking**: Multi-touchpoint attribution
- **Product Performance Analytics**: Sales trends and inventory optimization
- **Marketing Attribution**: Campaign effectiveness measurement
- **Operational Analytics**: Supply chain and fulfillment metrics

### Business Intelligence Frameworks
- **Customer Segmentation**: RFM analysis and behavioral clustering
- **Sales Forecasting**: Demand prediction and inventory planning
- **A/B Testing Analytics**: Experiment design and statistical analysis
- **Real-time Dashboards**: Live performance monitoring

## Practical Examples

### Customer Behavior Analysis
\`\`\`sql
-- Customer lifetime value calculation
WITH customer_metrics AS (
  SELECT 
    c.customer_id,
    c.registration_date,
    COUNT(DISTINCT o.order_id) as total_orders,
    SUM(o.total_amount) as total_spent,
    AVG(o.total_amount) as avg_order_value,
    MAX(o.order_date) as last_order_date,
    EXTRACT(DAYS FROM CURRENT_DATE - MAX(o.order_date)) as days_since_last_order
  FROM customers c
  LEFT JOIN orders o ON c.customer_id = o.customer_id
  WHERE c.registration_date >= '2023-01-01'
  GROUP BY c.customer_id, c.registration_date
),
rfm_analysis AS (
  SELECT 
    customer_id,
    total_orders,
    total_spent,
    days_since_last_order,
    NTILE(5) OVER (ORDER BY days_since_last_order ASC) as recency_score,
    NTILE(5) OVER (ORDER BY total_orders DESC) as frequency_score,
    NTILE(5) OVER (ORDER BY total_spent DESC) as monetary_score
  FROM customer_metrics
  WHERE total_orders > 0
)
SELECT 
  customer_id,
  total_orders,
  total_spent,
  recency_score,
  frequency_score,
  monetary_score,
  CASE 
    WHEN recency_score >= 4 AND frequency_score >= 4 AND monetary_score >= 4 THEN 'Champions'
    WHEN recency_score >= 3 AND frequency_score >= 3 AND monetary_score >= 3 THEN 'Loyal Customers'
    WHEN recency_score >= 3 AND frequency_score <= 2 THEN 'Potential Loyalists'
    WHEN recency_score <= 2 AND frequency_score >= 3 THEN 'At Risk'
    WHEN recency_score <= 2 AND frequency_score <= 2 THEN 'Hibernating'
    ELSE 'New Customers'
  END as customer_segment
FROM rfm_analysis;
\`\`\`

### Product Performance Analytics
\`\`\`sql
-- Product recommendation system data
WITH product_performance AS (
  SELECT 
    p.product_id,
    p.category_id,
    p.product_name,
    COUNT(oi.order_item_id) as units_sold,
    SUM(oi.quantity * oi.unit_price) as total_revenue,
    AVG(pr.rating) as avg_rating,
    COUNT(pr.review_id) as review_count,
    AVG(oi.unit_price) as avg_selling_price
  FROM products p
  LEFT JOIN order_items oi ON p.product_id = oi.product_id
  LEFT JOIN product_reviews pr ON p.product_id = pr.product_id
  WHERE oi.order_date >= CURRENT_DATE - INTERVAL '90 days'
  GROUP BY p.product_id, p.category_id, p.product_name
),
category_benchmarks AS (
  SELECT 
    category_id,
    AVG(units_sold) as category_avg_units,
    AVG(total_revenue) as category_avg_revenue,
    AVG(avg_rating) as category_avg_rating
  FROM product_performance
  GROUP BY category_id
)
SELECT 
  pp.product_id,
  pp.product_name,
  pp.units_sold,
  pp.total_revenue,
  pp.avg_rating,
  CASE 
    WHEN pp.units_sold > cb.category_avg_units * 1.5 THEN 'Star Product'
    WHEN pp.units_sold > cb.category_avg_units THEN 'Above Average'
    WHEN pp.units_sold > cb.category_avg_units * 0.5 THEN 'Average'
    ELSE 'Underperformer'
  END as performance_category
FROM product_performance pp
JOIN category_benchmarks cb ON pp.category_id = cb.category_id
ORDER BY pp.total_revenue DESC;
\`\`\`

## Advanced E-commerce Analytics

### Marketing Attribution
- **Multi-Touch Attribution**: Customer journey mapping
- **Campaign ROI Analysis**: Marketing spend effectiveness
- **Customer Acquisition Cost**: Channel performance comparison
- **Conversion Funnel Analysis**: Drop-off point identification

### Operational Intelligence
- **Inventory Optimization**: Stock level recommendations
- **Supply Chain Analytics**: Vendor performance tracking
- **Fulfillment Efficiency**: Shipping and delivery optimization
- **Returns Analysis**: Product quality and satisfaction insights
    `,
    estimatedTime: 95,
    difficulty: "intermediate",
    prerequisites: ["Intermediate SQL", "E-commerce domain knowledge", "Analytics concepts"],
    tags: ["E-commerce", "Analytics", "Customer Segmentation", "Business Intelligence", "RFM Analysis"],
    videoUrl: "https://www.youtube.com/embed/Yh4CrPHVBdE", // E-commerce SQL Analytics
    duration: "95 minutes"
  },
  {
    id: "manufacturing-iot-optimization",
    title: "Manufacturing & IoT Data Optimization",
    description: "Optimize manufacturing processes using IoT sensor data, predictive maintenance, and supply chain analytics with SQL.",
    order: 4,
    content: `
# Manufacturing & IoT Data Optimization

## Overview
Modern manufacturing relies on IoT sensors and data analytics to optimize production, predict maintenance needs, and ensure quality control. This module covers industrial data management and optimization techniques.

## Key Concepts

### Industrial IoT Architecture
- **Sensor Data Management**: Time-series data from production equipment
- **Predictive Maintenance**: Equipment failure prediction and prevention
- **Quality Control Analytics**: Statistical process control and defect detection
- **Supply Chain Optimization**: Material flow and inventory management

### Manufacturing Analytics
- **Overall Equipment Effectiveness (OEE)**: Production efficiency measurement
- **Statistical Process Control**: Quality monitoring and control
- **Demand Forecasting**: Production planning and scheduling
- **Energy Management**: Power consumption optimization

## Practical Examples

### Predictive Maintenance System
\`\`\`sql
-- Equipment health monitoring
CREATE TABLE sensor_readings (
  reading_id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  equipment_id VARCHAR(20) NOT NULL,
  sensor_type VARCHAR(50) NOT NULL,
  reading_value DECIMAL(10,4) NOT NULL,
  reading_timestamp TIMESTAMP NOT NULL,
  unit_of_measure VARCHAR(20),
  quality_flag BOOLEAN DEFAULT TRUE
);

-- Predictive maintenance analysis
WITH equipment_trends AS (
  SELECT 
    equipment_id,
    sensor_type,
    reading_timestamp,
    reading_value,
    AVG(reading_value) OVER (
      PARTITION BY equipment_id, sensor_type 
      ORDER BY reading_timestamp 
      ROWS BETWEEN 23 PRECEDING AND CURRENT ROW
    ) as moving_avg_24h,
    STDDEV(reading_value) OVER (
      PARTITION BY equipment_id, sensor_type 
      ORDER BY reading_timestamp 
      ROWS BETWEEN 23 PRECEDING AND CURRENT ROW
    ) as moving_stddev_24h
  FROM sensor_readings
  WHERE reading_timestamp >= CURRENT_TIMESTAMP - INTERVAL '7 days'
    AND quality_flag = TRUE
),
anomaly_detection AS (
  SELECT 
    equipment_id,
    sensor_type,
    reading_timestamp,
    reading_value,
    moving_avg_24h,
    ABS(reading_value - moving_avg_24h) / NULLIF(moving_stddev_24h, 0) as z_score,
    CASE 
      WHEN ABS(reading_value - moving_avg_24h) / NULLIF(moving_stddev_24h, 0) > 3 THEN 'CRITICAL'
      WHEN ABS(reading_value - moving_avg_24h) / NULLIF(moving_stddev_24h, 0) > 2 THEN 'WARNING'
      ELSE 'NORMAL'
    END as alert_level
  FROM equipment_trends
  WHERE moving_stddev_24h IS NOT NULL
)
SELECT 
  equipment_id,
  sensor_type,
  COUNT(*) as alert_count,
  MAX(z_score) as max_deviation,
  MAX(reading_timestamp) as last_alert_time
FROM anomaly_detection
WHERE alert_level IN ('WARNING', 'CRITICAL')
  AND reading_timestamp >= CURRENT_TIMESTAMP - INTERVAL '24 hours'
GROUP BY equipment_id, sensor_type
ORDER BY alert_count DESC, max_deviation DESC;
\`\`\`

### Production Efficiency Analysis
\`\`\`sql
-- Overall Equipment Effectiveness (OEE) calculation
WITH production_metrics AS (
  SELECT 
    equipment_id,
    production_date,
    planned_production_time,
    actual_production_time,
    units_produced,
    units_planned,
    defective_units,
    ideal_cycle_time
  FROM production_logs
  WHERE production_date >= CURRENT_DATE - INTERVAL '30 days'
),
oee_calculation AS (
  SELECT 
    equipment_id,
    production_date,
    -- Availability = (Actual Production Time / Planned Production Time)
    (actual_production_time::DECIMAL / NULLIF(planned_production_time, 0)) as availability,
    -- Performance = (Actual Units / (Actual Time / Ideal Cycle Time))
    (units_produced::DECIMAL / NULLIF((actual_production_time / NULLIF(ideal_cycle_time, 0)), 0)) as performance,
    -- Quality = (Good Units / Total Units)
    ((units_produced - defective_units)::DECIMAL / NULLIF(units_produced, 0)) as quality
  FROM production_metrics
  WHERE planned_production_time > 0
    AND units_produced > 0
)
SELECT 
  equipment_id,
  AVG(availability) as avg_availability,
  AVG(performance) as avg_performance,
  AVG(quality) as avg_quality,
  AVG(availability * performance * quality) as overall_oee,
  CASE 
    WHEN AVG(availability * performance * quality) >= 0.85 THEN 'World Class'
    WHEN AVG(availability * performance * quality) >= 0.60 THEN 'Acceptable'
    ELSE 'Needs Improvement'
  END as oee_rating
FROM oee_calculation
GROUP BY equipment_id
ORDER BY overall_oee DESC;
\`\`\`

## Advanced Manufacturing Analytics

### Supply Chain Optimization
- **Vendor Performance**: Delivery reliability and quality metrics
- **Inventory Turnover**: Material flow optimization
- **Demand Planning**: Production scheduling and capacity planning
- **Cost Analysis**: Total cost of ownership and profitability

### Quality Management
- **Six Sigma Analytics**: Process improvement and variation reduction
- **Root Cause Analysis**: Defect investigation and prevention
- **Customer Quality Metrics**: Warranty claims and satisfaction
- **Regulatory Compliance**: Industry standards and certifications
    `,
    estimatedTime: 100,
    difficulty: "advanced",
    prerequisites: ["Advanced SQL", "Manufacturing knowledge", "IoT concepts", "Statistical analysis"],
    tags: ["Manufacturing", "IoT", "Predictive Maintenance", "OEE", "Quality Control"],
    videoUrl: "https://www.youtube.com/embed/nWeW3sCmD2k", // Manufacturing IoT SQL
    duration: "100 minutes"
  },
  {
    id: "media-entertainment-analytics",
    title: "Media & Entertainment Content Analytics",
    description: "Analyze media consumption patterns, content performance, and audience engagement using SQL for streaming and entertainment platforms.",
    order: 5,
    content: `
# Media & Entertainment Content Analytics

## Overview
Media and entertainment companies rely on data analytics to understand audience behavior, optimize content delivery, and maximize engagement. This module covers content analytics and audience insights.

## Key Concepts

### Content Performance Metrics
- **Viewership Analytics**: Watch time, completion rates, and engagement
- **Content Recommendation**: Personalization and discovery algorithms
- **Audience Segmentation**: Demographics and behavioral analysis
- **Revenue Optimization**: Subscription and advertising analytics

### Streaming Platform Analytics
- **Content Delivery Optimization**: CDN performance and quality metrics
- **User Experience Analytics**: Buffering, load times, and quality scores
- **Churn Prediction**: Subscription retention modeling
- **Content Acquisition**: ROI analysis for content investments

## Practical Examples

### Content Performance Dashboard
\`\`\`sql
-- Content engagement analysis
WITH content_metrics AS (
  SELECT 
    c.content_id,
    c.title,
    c.genre,
    c.release_date,
    c.duration_minutes,
    COUNT(DISTINCT v.user_id) as unique_viewers,
    AVG(v.watch_duration_minutes) as avg_watch_time,
    AVG(v.watch_duration_minutes / c.duration_minutes) as completion_rate,
    SUM(v.watch_duration_minutes) as total_watch_time,
    COUNT(r.rating) as rating_count,
    AVG(r.rating) as avg_rating
  FROM content c
  LEFT JOIN viewing_sessions v ON c.content_id = v.content_id
  LEFT JOIN ratings r ON c.content_id = r.content_id
  WHERE v.session_date >= CURRENT_DATE - INTERVAL '30 days'
  GROUP BY c.content_id, c.title, c.genre, c.release_date, c.duration_minutes
),
genre_benchmarks AS (
  SELECT 
    genre,
    AVG(completion_rate) as genre_avg_completion,
    AVG(avg_rating) as genre_avg_rating,
    AVG(unique_viewers) as genre_avg_viewers
  FROM content_metrics
  GROUP BY genre
)
SELECT 
  cm.content_id,
  cm.title,
  cm.genre,
  cm.unique_viewers,
  cm.avg_watch_time,
  cm.completion_rate,
  cm.avg_rating,
  CASE 
    WHEN cm.completion_rate > gb.genre_avg_completion * 1.2 THEN 'High Engagement'
    WHEN cm.completion_rate > gb.genre_avg_completion THEN 'Above Average'
    WHEN cm.completion_rate > gb.genre_avg_completion * 0.8 THEN 'Average'
    ELSE 'Below Average'
  END as engagement_category,
  CASE 
    WHEN cm.unique_viewers > gb.genre_avg_viewers * 1.5 THEN 'Viral Hit'
    WHEN cm.unique_viewers > gb.genre_avg_viewers THEN 'Popular'
    ELSE 'Niche'
  END as popularity_tier
FROM content_metrics cm
JOIN genre_benchmarks gb ON cm.genre = gb.genre
ORDER BY cm.total_watch_time DESC;
\`\`\`

### Audience Segmentation & Personalization
\`\`\`sql
-- User behavior clustering for recommendations
WITH user_preferences AS (
  SELECT 
    u.user_id,
    u.subscription_tier,
    COUNT(DISTINCT v.content_id) as content_variety,
    AVG(v.watch_duration_minutes) as avg_session_length,
    STRING_AGG(DISTINCT c.genre, ', ') as preferred_genres,
    COUNT(CASE WHEN v.watch_duration_minutes / c.duration_minutes >= 0.8 THEN 1 END) as completed_content,
    COUNT(CASE WHEN DATE_PART('hour', v.session_start) BETWEEN 18 AND 23 THEN 1 END) as evening_sessions,
    COUNT(CASE WHEN DATE_PART('dow', v.session_date) IN (0,6) THEN 1 END) as weekend_sessions
  FROM users u
  LEFT JOIN viewing_sessions v ON u.user_id = v.user_id
  LEFT JOIN content c ON v.content_id = c.content_id
  WHERE v.session_date >= CURRENT_DATE - INTERVAL '90 days'
  GROUP BY u.user_id, u.subscription_tier
),
user_segments AS (
  SELECT 
    user_id,
    subscription_tier,
    content_variety,
    avg_session_length,
    preferred_genres,
    CASE 
      WHEN avg_session_length > 60 AND content_variety > 20 THEN 'Binge Watcher'
      WHEN evening_sessions > weekend_sessions * 2 THEN 'Weekday Viewer'
      WHEN weekend_sessions > evening_sessions THEN 'Weekend Warrior'
      WHEN content_variety > 15 AND completed_content / content_variety > 0.7 THEN 'Content Explorer'
      WHEN avg_session_length < 30 THEN 'Quick Browser'
      ELSE 'Casual Viewer'
    END as viewer_segment
  FROM user_preferences
  WHERE content_variety > 0
)
SELECT 
  viewer_segment,
  subscription_tier,
  COUNT(*) as user_count,
  AVG(content_variety) as avg_content_variety,
  AVG(avg_session_length) as avg_session_duration,
  MODE() WITHIN GROUP (ORDER BY preferred_genres) as most_common_genres
FROM user_segments
GROUP BY viewer_segment, subscription_tier
ORDER BY user_count DESC;
\`\`\`

## Advanced Media Analytics

### Content Acquisition Strategy
- **Content ROI Analysis**: Investment vs. engagement metrics
- **Competitive Analysis**: Market positioning and content gaps
- **Trend Prediction**: Emerging genres and formats
- **Licensing Optimization**: Content portfolio management

### Revenue Analytics
- **Subscription Analytics**: Churn prediction and retention strategies
- **Advertising Performance**: Ad engagement and revenue optimization
- **Pricing Strategy**: Subscription tier analysis and optimization
- **Market Expansion**: Geographic and demographic growth opportunities
    `,
    estimatedTime: 80,
    difficulty: "intermediate",
    prerequisites: ["Intermediate SQL", "Media industry knowledge", "Analytics concepts"],
    tags: ["Media", "Entertainment", "Content Analytics", "User Behavior", "Recommendations"],
    videoUrl: "https://www.youtube.com/embed/BPHAr4QGGVE", // Media Analytics SQL
    duration: "80 minutes"
  },
  {
    id: "government-public-sector",
    title: "Government & Public Sector Data Management",
    description: "Implement secure government data systems with transparency requirements, citizen services analytics, and public policy insights using SQL.",
    order: 6,
    content: `
# Government & Public Sector Data Management

## Overview
Government and public sector organizations require secure, transparent, and efficient data management systems. This module covers citizen services, policy analytics, and regulatory compliance in the public sector.

## Key Concepts

### Public Sector Data Requirements
- **Data Transparency**: Open data initiatives and public access
- **Citizen Privacy**: Personal information protection and GDPR compliance
- **Service Delivery Analytics**: Government service efficiency and effectiveness
- **Policy Impact Analysis**: Evidence-based policy making

### Government Data Architecture
- **Citizen Services Platforms**: Digital government service delivery
- **Regulatory Compliance**: Legal and audit requirements
- **Emergency Response Systems**: Crisis management and public safety
- **Budget and Financial Analytics**: Public spending transparency

## Practical Examples

### Citizen Services Analytics
\`\`\`sql
-- Government service delivery performance
CREATE TABLE service_requests (
  request_id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  citizen_id VARCHAR(20), -- Anonymized citizen identifier
  service_type VARCHAR(50) NOT NULL,
  request_date TIMESTAMP NOT NULL,
  assigned_department VARCHAR(50),
  priority_level VARCHAR(10) DEFAULT 'NORMAL',
  status VARCHAR(20) DEFAULT 'SUBMITTED',
  resolution_date TIMESTAMP,
  satisfaction_rating INTEGER,
  processing_cost DECIMAL(10,2)
);

-- Service delivery efficiency analysis
WITH service_metrics AS (
  SELECT 
    service_type,
    assigned_department,
    COUNT(*) as total_requests,
    AVG(EXTRACT(DAYS FROM resolution_date - request_date)) as avg_resolution_days,
    COUNT(CASE WHEN status = 'RESOLVED' THEN 1 END) as resolved_requests,
    AVG(satisfaction_rating) as avg_satisfaction,
    SUM(processing_cost) as total_cost
  FROM service_requests
  WHERE request_date >= CURRENT_DATE - INTERVAL '12 months'
  GROUP BY service_type, assigned_department
),
department_benchmarks AS (
  SELECT 
    assigned_department,
    AVG(avg_resolution_days) as dept_avg_resolution,
    AVG(avg_satisfaction) as dept_avg_satisfaction
  FROM service_metrics
  GROUP BY assigned_department
)
SELECT 
  sm.service_type,
  sm.assigned_department,
  sm.total_requests,
  sm.avg_resolution_days,
  sm.resolved_requests::DECIMAL / sm.total_requests as resolution_rate,
  sm.avg_satisfaction,
  sm.total_cost / sm.total_requests as cost_per_request,
  CASE 
    WHEN sm.avg_resolution_days <= db.dept_avg_resolution * 0.8 THEN 'Excellent'
    WHEN sm.avg_resolution_days <= db.dept_avg_resolution THEN 'Good'
    WHEN sm.avg_resolution_days <= db.dept_avg_resolution * 1.2 THEN 'Average'
    ELSE 'Needs Improvement'
  END as efficiency_rating
FROM service_metrics sm
JOIN department_benchmarks db ON sm.assigned_department = db.assigned_department
ORDER BY sm.total_requests DESC;
\`\`\`

### Budget Transparency & Analysis
\`\`\`sql
-- Public spending analysis and transparency
WITH budget_analysis AS (
  SELECT 
    fiscal_year,
    department,
    program_category,
    SUM(allocated_amount) as total_allocated,
    SUM(spent_amount) as total_spent,
    SUM(allocated_amount) - SUM(spent_amount) as variance,
    COUNT(DISTINCT project_id) as number_of_projects
  FROM budget_allocations ba
  JOIN expenditures e ON ba.allocation_id = e.allocation_id
  WHERE fiscal_year >= EXTRACT(YEAR FROM CURRENT_DATE) - 3
  GROUP BY fiscal_year, department, program_category
),
spending_trends AS (
  SELECT 
    department,
    program_category,
    fiscal_year,
    total_spent,
    LAG(total_spent) OVER (
      PARTITION BY department, program_category 
      ORDER BY fiscal_year
    ) as previous_year_spent,
    total_spent - LAG(total_spent) OVER (
      PARTITION BY department, program_category 
      ORDER BY fiscal_year
    ) as year_over_year_change
  FROM budget_analysis
)
SELECT 
  department,
  program_category,
  fiscal_year,
  total_spent,
  previous_year_spent,
  year_over_year_change,
  CASE 
    WHEN previous_year_spent IS NULL THEN 'New Program'
    WHEN year_over_year_change > previous_year_spent * 0.1 THEN 'Significant Increase'
    WHEN year_over_year_change < previous_year_spent * -0.1 THEN 'Significant Decrease'
    ELSE 'Stable'
  END as spending_trend,
  ROUND((year_over_year_change / NULLIF(previous_year_spent, 0)) * 100, 2) as percent_change
FROM spending_trends
WHERE fiscal_year = EXTRACT(YEAR FROM CURRENT_DATE)
ORDER BY ABS(year_over_year_change) DESC NULLS LAST;
\`\`\`

## Advanced Public Sector Analytics

### Policy Impact Assessment
- **Outcome Measurement**: Program effectiveness and citizen impact
- **Cost-Benefit Analysis**: Return on public investment
- **Demographic Analysis**: Service equity and accessibility
- **Long-term Trend Analysis**: Policy sustainability and planning

### Emergency Response & Public Safety
- **Crisis Management**: Emergency resource allocation and response
- **Public Health Analytics**: Disease surveillance and health outcomes
- **Infrastructure Monitoring**: Public asset management and maintenance
- **Risk Assessment**: Threat analysis and preparedness planning
    `,
    estimatedTime: 75,
    difficulty: "advanced",
    prerequisites: ["Advanced SQL", "Public administration knowledge", "Data governance concepts"],
    tags: ["Government", "Public Sector", "Transparency", "Citizen Services", "Policy Analysis"],
    videoUrl: "https://www.youtube.com/embed/UcpHuYJAafI", // Government Data SQL
    duration: "75 minutes"
  }
];
